/**
 * Click & Collect — PLP variant availability checker.
 *
 * Fetches inventory data for product cards on collection/search pages
 * and disables variant buttons that have no stock at the allowed locations
 * (warehouse-only when no store selected, warehouse + store + nearby otherwise).
 *
 * @module cc-card-availability
 */
import {
  fetchInventory,
  extractLocations,
  getAllowedGids,
  debounce,
  hasLocalPickup,
  isWarehouseLocation,
  readCCState,
} from '@cc/utils';

// Concurrency kept low: /apps/proxy is behind Cloudflare which rate-limits
// bursts of parallel requests from one client.
const CONCURRENCY = 2;
const PROCESSED_ATTR = 'data-cc-checked';

// Per-card raw inventory data, so store changes can re-interpret without
// triggering a new fetch for every card on the page.
const cardDataCache = new WeakMap();

/**
 * Apply availability state to a single card's variant buttons.
 * Safe to call repeatedly with different `allowedGids` — toggles only
 * buttons we marked ourselves (`cc-variant-unavailable`) and never touches
 * buttons that were disabled by the theme (e.g. sold-out variants).
 *
 * @param {HTMLElement} card
 * @param {object} data - raw inventory response
 * @param {Set<string>} allowedSet
 */
function applyCardData(card, data, allowedSet) {
  const warehouseGid = readCCState()?.warehouseGid || null;
  // A store only counts when its location has local pickup on; the
  // warehouse ships orders, so it never needs it.
  const canFulfil = (loc) => isWarehouseLocation(loc, warehouseGid) || hasLocalPickup(loc);

  const buttons = card.querySelectorAll('[data-variant-id]');
  for (const button of buttons) {
    const wasOurs = button.classList.contains('cc-variant-unavailable');
    const themeDisabled =
      button.classList.contains('product-form__variant-button-disabled') && !wasOurs;
    if (themeDisabled) continue;

    const locations = extractLocations(data, button.dataset.variantId) || [];
    const available = locations.some(
      (loc) => allowedSet.has(loc.id) && loc.available > 0 && canFulfil(loc),
    );

    if (!available) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.remove('product-form__variant-button', 'lg:hover:!border-gray-800');
      button.classList.add(
        'product-form__variant-button-disabled',
        'text-gray-500',
        'lg:hover:!border-gray-500',
        'cc-variant-unavailable',
      );
    } else if (wasOurs) {
      button.removeAttribute('aria-disabled');
      button.classList.remove(
        'product-form__variant-button-disabled',
        'cc-variant-unavailable',
        'text-gray-500',
        'lg:hover:!border-gray-500',
      );
      button.classList.add('product-form__variant-button', 'lg:hover:!border-gray-800');
    }
  }
}

/**
 * Fetch (or reuse cached) inventory for a card and apply availability.
 * @param {HTMLElement} card - A <card-product-add-to-cart> element
 * @param {Set<string>} allowedSet
 */
async function processCard(card, allowedSet) {
  const productId = card.dataset.sectionId;
  if (!productId) return;
  if (!card.querySelectorAll('[data-variant-id]').length) return;

  let data = cardDataCache.get(card);
  if (!data) {
    data = await fetchInventory(productId);
    if (!data) return;
    cardDataCache.set(card, data);
  }

  applyCardData(card, data, allowedSet);
  card.setAttribute(PROCESSED_ATTR, '');
}

/**
 * Find unprocessed cards and check their variant availability.
 * Processes in batches to limit concurrent requests.
 */
async function checkCards() {
  const allowedGids = getAllowedGids();
  if (!allowedGids) return;

  const cards = document.querySelectorAll(
    `card-product-add-to-cart:not([${PROCESSED_ATTR}])`,
  );
  if (!cards.length) return;

  const allowedSet = new Set(allowedGids);
  const queue = Array.from(cards);

  while (queue.length > 0) {
    const batch = queue.splice(0, CONCURRENCY);
    await Promise.all(batch.map((card) => processCard(card, allowedSet).catch(() => {})));
  }
}

/**
 * Re-interpret already-processed cards against the current allowed locations
 * without refetching inventory. Used on store change to avoid burst /apps/proxy
 * requests that trigger Cloudflare rate-limiting.
 */
function reinterpretCards() {
  const allowedGids = getAllowedGids();
  if (!allowedGids) return;

  const allowedSet = new Set(allowedGids);
  const cards = document.querySelectorAll(`card-product-add-to-cart[${PROCESSED_ATTR}]`);
  for (const card of cards) {
    const data = cardDataCache.get(card);
    if (data) applyCardData(card, data, allowedSet);
  }
}

// --- Lifecycle ---

// Multiple grid/section events can fire back-to-back during infinite scroll
// and section reloads — debounce so checkCards runs once per burst.
const debouncedCheckCards = debounce(checkCards, 50);

function onStoreChange() {
  // Re-interpret cached data first (no network), then pick up any cards
  // that haven't been processed yet (e.g. just loaded via infinite scroll).
  reinterpretCards();
  debouncedCheckCards();
}

document.addEventListener('DOMContentLoaded', debouncedCheckCards);
document.addEventListener('product-grid-updated', debouncedCheckCards);
document.addEventListener('card-product-loaded', debouncedCheckCards);
document.addEventListener('recently-viewed-products-loaded', debouncedCheckCards);
document.addEventListener('related-products-loaded', debouncedCheckCards);
window.addEventListener('zippy:cc-store-selector:change', onStoreChange);
